import React, { useState, useEffect } from 'react';
import { MarketLink as Link, MarketNavLink as NavLink } from './MarketLink';
import { Search, User, ShoppingBag, Menu, Award, Moon, Sun, Monitor, Camera, Mic, LogOut } from 'lucide-react';
import RegionSelector from './RegionSelector';
import LanguageToggle from './LanguageToggle';
import CurrencySelector from './CurrencySelector';
import { getTranslation as t, isRTL } from '../utils/translations';
import { usePlugins } from '../context/PluginContext';
import { useAuth } from '../context/AuthContext';
import MobileSidebar from './MobileSidebar';
import { liveAPI, API_BASE, getCategories } from '../api/client';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const Navbar = ({ cartCount = 0, onSearchClick, storeSettings, isDarkMode, toggleTheme, themeMode = 'auto' }) => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const navbarFileInputRef = React.useRef(null);
    const { isPluginActive, pluginsLoaded } = usePlugins();
    const { isAuthenticated, logout, user } = useAuth();
    const [categories, setCategories] = useState([]);
    const [avatarError, setAvatarError] = useState(false);
    const isRtl = isRTL();

    const fallbackCategories = [
        { id: 'temp-1', name: 'Watches', nameAr: 'ساعات فاخرة', nameEn: 'Luxury Watches' },
        { id: 'temp-2', name: 'Jewelry', nameAr: 'مجوهرات راقية', nameEn: 'Fine Jewelry' },
        { id: 'temp-3', name: 'Leather-Goods', nameAr: 'حقائب وجلود', nameEn: 'Leather Goods' },
        { id: 'temp-4', name: 'Apparel', nameAr: 'ملابس وأزياء', nameEn: 'Haute Couture' },
        { id: 'temp-5', name: 'Supercars', nameAr: 'سيارات خارقة', nameEn: 'Supercars' },
        { id: 'temp-6', name: 'Yachts', nameAr: 'يخوت وطيران', nameEn: 'Yachts & Jets' },
        { id: 'temp-7', name: 'Electronics', nameAr: 'إلكترونيات مبتكرة', nameEn: 'Electronics' },
        { id: 'temp-8', name: 'Sourcing', nameAr: 'الاستيراد الخاص', nameEn: 'Bespoke Sourcing' },
        { id: 'temp-9', name: 'Collectibles', nameAr: 'قطع فنية نادرة', nameEn: 'Fine Art' },
        { id: 'temp-10', name: 'Home-Living', nameAr: 'أثاث وديكور فاخر', nameEn: 'Luxury Living' }
    ];

    useEffect(() => {
        getCategories().then(c => {
            if (Array.isArray(c)) setCategories(c);
        }).catch(() => {});
    }, []);

    const [forcedHidden, setForcedHidden] = useState(false);
    const [activeBroadcastId, setActiveBroadcastId] = useState(null);

    useEffect(() => {
        let interval;
        const checkLiveStatus = async () => {
            try {
                const data = await liveAPI.getActiveRooms();
                const broadcasts = (data || []).filter(r => r.status === 'ACTIVE' || r.type === 'BROADCAST' || r.type === 'LIVE_COMMERCE' || r.type === 'LIVE');
                if (broadcasts.length > 0) {
                    setActiveBroadcastId(broadcasts[0].id);
                } else {
                    setActiveBroadcastId(null);
                }
            } catch (e) {
                console.error('Failed checking live status', e);
            }
        };

        checkLiveStatus();
        interval = setInterval(checkLiveStatus, 30000);

        const stompClient = new Client({
            webSocketFactory: () => new SockJS(`${API_BASE}/ws/chat`),
            onConnect: () => {
                stompClient.subscribe('/topic/live-rooms', () => {
                    checkLiveStatus();
                });
                stompClient.subscribe('/topic/products', () => {
                    window.dispatchEvent(new CustomEvent('sync-storefront-data', { detail: { type: 'products' } }));
                });
                stompClient.subscribe('/topic/videos', () => {
                    window.dispatchEvent(new CustomEvent('sync-storefront-data', { detail: { type: 'videos' } }));
                });
                stompClient.subscribe('/topic/categories', () => {
                    window.dispatchEvent(new CustomEvent('sync-storefront-data', { detail: { type: 'categories' } }));
                });
            }
        });
        stompClient.activate();

        return () => {
            clearInterval(interval);
            stompClient.deactivate();
        };
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        const onVideoScroll = (e) => {
            if (e.detail === 'down') setForcedHidden(true);
            else if (e.detail === 'up') setForcedHidden(false);
        };
        
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('video-scroll', onVideoScroll);
        
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('video-scroll', onVideoScroll);
        };
    }, []);

    return (
        <>
            {/* ── Desktop Header System ── */}
            <nav className={`glass-nav nav-desktop-only ${scrolled ? 'nav-scrolled' : ''} ${forcedHidden ? 'nav-hidden-by-scroll' : ''}`}>
                {/* Tier 1: Top Utility Bar (Desktop Only) */}
                <div className="nav-top-utility-bar nav-desktop-only">
                    <div className="container top-bar-inner">
                        <div className="top-bar-left">
                            <span className="nav-language-wrapper"><LanguageToggle /></span>
                            <span className="nav-region-wrapper"><RegionSelector /></span>
                            <span className="nav-trust-chip">{t('SECURE_CHECKOUT')}</span>
                        </div>
                        <div className="top-bar-right">
                            {pluginsLoaded && isPluginActive('loyalty_program') && (
                                <div className="nav-loyalty-chip" title="Loyalty Points">
                                    <Award size={13} />
                                    <span>3,400 pts</span>
                                </div>
                            )}
                            {isAuthenticated && <Link to="/orders" className="top-bar-link">{t('ORDERS')}</Link>}
                            <Link 
                                to="/seller/dashboard" 
                                className="top-bar-link seller-portal-link"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    color: '#111111',
                                    fontWeight: '700',
                                    padding: '3px 10px',
                                    borderRadius: '6px',
                                    background: '#f5f5f5',
                                    border: '1px solid #e5e5e5',
                                    margin: '0 6px',
                                    textDecoration: 'none',
                                    fontSize: '11px',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                <span>🏪</span>
                                <span>{isRtl ? 'بوابة البائع / المورد' : 'ENTER AS SELLER'}</span>
                            </Link>
                            <span className="top-bar-welcome">
                                {isAuthenticated ? `${t('WELCOME_TO_LUXVONO')}, ${user?.fullName || user?.firstName || user?.email || ''}` : t('WELCOME_TO_LUXVONO')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Tier 2: Main Search & Brand Bar */}
                <div className="nav-main-search-bar">
                    <div className="container main-bar-inner">
                        {/* Main Brand Logo */}
                        <Link to="/" className="navbar-brand logo premium-gradient">
                            {storeSettings?.logoUrl ? (
                                <img
                                    src={storeSettings.logoUrl}
                                    alt={storeSettings.logoText || 'Luxvono'}
                                    className="navbar-logo-image"
                                    loading="lazy"
                                />
                            ) : (
                                storeSettings?.logoText || 'LUXVONO'
                            )}
                        </Link>

                        {/* Wide Desktop Alibaba-Style Search Bar */}
                        <div className="navbar-search-bar-desktop nav-desktop-only" onClick={() => onSearchClick('text')}>
                            <Search size={18} className="search-icon" />
                            <span className="search-placeholder">
                                {isRtl ? 'ابحث بالمعرّف #ID، أو المتجر، أو الصديق @، أو المنتجات...' : 'Search by #ID, store, friend @, or products...'}
                            </span>
                            <div className="search-tools-right">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (navbarFileInputRef.current) {
                                            navbarFileInputRef.current.value = '';
                                            navbarFileInputRef.current.click();
                                        }
                                    }}
                                    aria-label="Search by image"
                                    className="search-tool-btn"
                                >
                                    <Camera size={16} />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSearchClick('voice');
                                    }}
                                    aria-label="Search by voice"
                                    className="search-tool-btn"
                                >
                                    <Mic size={16} />
                                </button>
                            </div>
                        </div>

                        <input
                            type="file"
                            ref={navbarFileInputRef}
                            style={{ display: 'none' }}
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    const url = URL.createObjectURL(file);
                                    onSearchClick('camera', url);
                                }
                            }}
                        />

                        <div className="nav-icons">
                            <button 
                                type="button"
                                onClick={toggleTheme} 
                                aria-label="Toggle Theme" 
                                className="theme-toggle-btn nav-desktop-only" 
                                title={`Theme: ${themeMode || (isDarkMode ? 'dark' : 'light')}`}
                                style={{
                                    background: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.25)' : '1px solid rgba(0, 0, 0, 0.08)',
                                    color: isDarkMode ? '#ffffff' : '#111827',
                                    borderRadius: '50%',
                                    width: '34px',
                                    height: '34px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    marginInlineEnd: '4px'
                                }}
                            >
                                {isDarkMode ? <Sun size={17} strokeWidth={2} /> : <Moon size={17} strokeWidth={2} />}
                            </button>

                            {isAuthenticated ? (
                                <div className="nav-auth-wrap nav-desktop-only" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Link 
                                        to="/account/profile" 
                                        aria-label="Settings" 
                                        className="nav-profile-link" 
                                        title="Account Settings"
                                    >
                                        {user?.profileImageUrl && !avatarError ? (
                                            <img src={user.profileImageUrl} alt="Avatar" onError={() => setAvatarError(true)} style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.1)' }} />
                                        ) : (
                                            <User size={20} strokeWidth={1.5} />
                                        )}
                                    </Link>
                                    <button onClick={logout} aria-label="Logout" className="nav-logout-btn" title={t('LOGOUT') || 'Logout'}>
                                        <LogOut size={13} className="nav-logout-icon" />
                                        <span className="nav-logout-text">{t('LOGOUT')}</span>
                                    </button>
                                </div>
                            ) : (
                                <Link 
                                    to="/login" 
                                    aria-label="Account" 
                                    className="nav-desktop-only"
                                >
                                    <User size={20} strokeWidth={1.5} />
                                </Link>
                            )}

                            <Link to="/cart" id="nav-cart-icon" className="nav-cart-icon" aria-label="Cart">
                                <ShoppingBag size={20} strokeWidth={1.5} />
                                {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Tier 3: Secondary Categories & Nav Bar (Desktop Only) */}
                <div className="nav-secondary-nav-bar nav-desktop-only">
                    <div className="container sub-bar-inner">
                        <div 
                            className="nav-categories-trigger"
                            onClick={() => setMenuOpen(true)}
                            role="button"
                            tabIndex={0}
                            style={{ cursor: 'pointer' }}
                            title={t('ALL_CATEGORIES') || 'All Categories'}
                        >
                            <Menu size={15} />
                            <span>{t('ALL_CATEGORIES') || 'All Categories'}</span>
                        </div>
                        <div className="nav-links">
                            <Link to="/">{t('HOME')}</Link>
                            <Link to="/category/all">{t('COLLECTIONS')}</Link>
                            <Link to="/stores">{isRtl ? 'المتاجر والوكلاء' : 'Stores & Sellers'}</Link>
                            <Link to="/following">{isRtl ? 'أتابعهم' : 'Following'}</Link>
                            <Link to={activeBroadcastId ? `/live/${activeBroadcastId}` : "/live"} className="relative flex items-center gap-1.5">
                                {t('NAV_LIVE')}
                                {activeBroadcastId && (
                                    <span className="flex h-2.5 w-2.5 relative">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]"></span>
                                    </span>
                                )}
                            </Link>
                            <Link to="/videos">
                                {isRtl ? 'الفيديوهات' : (t('VIDEO_COMMERCE') || 'Videos')}
                            </Link>
                            <Link to="/magazine">{t('NAV_MAGAZINE')}</Link>
                            <Link to="/sourcing">{t('NAV_SOURCING')}</Link>
                            <Link to="/vip">{t('NAV_VIP')}</Link>
                            <Link 
                                to="/seller/dashboard" 
                                style={{
                                    background: '#000000',
                                    border: '1px solid #000000',
                                    color: '#ffffff',
                                    padding: '5px 14px',
                                    borderRadius: '20px',
                                    fontWeight: '700',
                                    fontSize: '12px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    textDecoration: 'none',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}
                            >
                                <span>🏛️</span>
                                <span>{isRtl ? 'بوابة التجار (SELLER)' : 'SELLER / VENDOR'}</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* ── Rebuilt Dedicated Mobile Header System ── */}
            <nav className={`mobile-nav nav-mobile-only ${scrolled ? 'nav-scrolled' : ''} ${forcedHidden ? 'nav-hidden-by-scroll' : ''}`}>
                {/* Mobile Top Utility Bar */}
                <div className="mobile-top-utility-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0 6px', width: '100%', maxHeight: '40px', flexWrap: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LanguageToggle />
                        <RegionSelector />
                    </div>
                </div>

                {/* ROW 1: ☰ LUXVONO (Live Badge) | Region Chat Cart (matching Android TopAppBar) */}
                <div className="mobile-header-row-1">
                    <button 
                        id="mobile-hamburger-btn"
                        className="mobile-hamburger" 
                        onClick={() => setMenuOpen(true)} 
                        aria-label="Menu"
                    >
                        <Menu size={22} strokeWidth={2} />
                    </button>

                    <Link to="/" className="mobile-logo logo">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {storeSettings?.logoUrl ? (
                                <img
                                    src={storeSettings.logoUrl}
                                    alt={storeSettings.logoText || 'Luxvono'}
                                    className="navbar-logo-image"
                                    loading="lazy"
                                />
                            ) : (
                                <span style={{ fontWeight: '800', letterSpacing: '2px', fontSize: '18px' }}>
                                    {storeSettings?.logoText || 'LUXVONO'}
                                </span>
                            )}
                            {activeBroadcastId && (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    padding: '2px 6px',
                                    background: '#FE2C55',
                                    color: '#ffffff',
                                    fontSize: '9px',
                                    fontWeight: '800',
                                    borderRadius: '4px',
                                    letterSpacing: '0.5px'
                                }}>
                                    <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#ffffff', animation: 'pulse 1s infinite' }} />
                                    LIVE
                                </span>
                            )}
                        </div>
                    </Link>

                    <div className="mobile-header-right-icons">
                        <button 
                            type="button" 
                            onClick={() => window.dispatchEvent(new CustomEvent('luxvono-chat:open', { detail: { tab: 'support' } }))}
                            className="mobile-chat-trigger-btn"
                            aria-label="Support Chat"
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'inherit',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            <User size={20} strokeWidth={1.8} />
                        </button>

                        <Link to="/cart" id="mobile-nav-cart-icon" className="mobile-cart-icon" aria-label="Cart">
                            <ShoppingBag size={20} strokeWidth={1.8} />
                            {cartCount > 0 && <span className="mobile-cart-badge">{cartCount}</span>}
                        </Link>
                    </div>
                </div>

                {/* ROW 2: Compact & Professional Mobile Search Bar — Exactly like Computer */}
                <div className="mobile-header-search-row">
                    <div 
                        className="navbar-search-bar-mobile" 
                        onClick={() => onSearchClick('text')}
                        role="button"
                        tabIndex={0}
                        aria-label={t('SEARCH_PROMPT') || 'Search products, brands, categories...'}
                    >
                        <Search size={16} className="search-icon mobile-search-glass-icon" />
                        <span className="search-placeholder mobile-search-placeholder-text">
                            {isRtl ? 'ابحث بالمعرّف #ID، المتجر، الصديق @...' : 'Search #ID, store, friend @...'}
                        </span>
                        <div className="search-tools-right mobile-search-tools-group">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (navbarFileInputRef.current) {
                                        navbarFileInputRef.current.value = '';
                                        navbarFileInputRef.current.click();
                                    }
                                }}
                                aria-label="Search by image"
                                className="search-tool-btn mobile-search-tool-icon"
                                title="Search by image"
                            >
                                <Camera size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSearchClick('voice');
                                }}
                                aria-label="Search by voice"
                                className="search-tool-btn mobile-search-tool-icon"
                                title="Search by voice"
                            >
                                <Mic size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* ROW 3: Horizontal scroll category chips */}
                <div className="mobile-header-row-3">
                    <div className="horizontal-category-chips">
                        {(categories.length > 0 ? categories : fallbackCategories).map((cat) => {
                            const catName = isRTL()
                                ? (cat.nameAr || cat.nameEn || cat.name || '')
                                : (cat.nameEn || cat.nameAr || cat.name || '');
                            return (
                                <NavLink
                                    key={cat.id || cat.name}
                                    to={`/category/${cat.name}`}
                                    className={({ isActive }) => `category-chip-pill ${isActive ? 'active' : ''}`}
                                >
                                    {catName}
                                </NavLink>
                            );
                        })}
                    </div>
                </div>
            </nav>

            {/* ── Dedicated Horizontally Scrollable Benefits Bar ── */}
            <div className="benefits-bar">
                <div className="benefits-bar-inner">
                    <span className="benefit-item">🛡️ {t('SECURE_CHECKOUT') || 'Secure Payment'}</span>
                    <span className="benefit-item">✈️ {t('WORLDWIDE_SHIPPING') || 'Free Worldwide Shipping'}</span>
                    <span className="benefit-item">🛎️ {t('CONCIERGE_24_7') || '24/7 Concierge'}</span>
                    
                    {/* Duplicate for seamless infinite marquee on mobile */}
                    <span className="benefit-item mobile-marquee-duplicate">🛡️ {t('SECURE_CHECKOUT') || 'Secure Payment'}</span>
                    <span className="benefit-item mobile-marquee-duplicate">✈️ {t('WORLDWIDE_SHIPPING') || 'Free Worldwide Shipping'}</span>
                    <span className="benefit-item mobile-marquee-duplicate">🛎️ {t('CONCIERGE_24_7') || '24/7 Concierge'}</span>
                </div>
            </div>
            <MobileSidebar
                isOpen={menuOpen}
                onClose={() => setMenuOpen(false)}
                storeSettings={storeSettings}
                isDarkMode={isDarkMode}
                toggleTheme={toggleTheme}
                themeMode={themeMode}
                activeBroadcastId={activeBroadcastId}
            />
        </>
    );
};

export default Navbar;
